/*
  IRON Attach 3D media viewer.

  Ported from the offline prototype at
  products\ia-tb-100t\runs\web3d\preview\index.html. The behaviour is the same
  one tested there; what changed is that the stops, the positions and the model
  URL all arrive as data rather than being written into the file, so a second
  product needs a JSON file and a line in the theme setting, not a second copy
  of this script.

  Nothing here runs until the tile is clicked. model-viewer is about 400 KB and
  the overwhelming majority of visitors never open the viewer, so importing it
  on page load would be charging every one of them for a feature few use. The
  tile is static markup until then.
*/

(function () {
  'use strict';

  var libPromise = null;

  // A <script type="module"> tag rather than a dynamic import(). Shopify
  // minifies theme JS on the way out of the CDN and rewrites import() into
  // require(), which does not exist in a browser, so the dynamic form dies
  // with "require is not defined" the first time anyone clicks the tile. It
  // works perfectly in local testing, where nothing minifies it. A tag is
  // not a construct a minifier can rewrite.
  function loadLib(url) {
    if (!libPromise) {
      libPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.type = 'module';
        s.src = url;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('could not load ' + url)); };
        document.head.appendChild(s);
      }).then(function () {
        return window.customElements
          ? customElements.whenDefined('model-viewer')
          : null;
      });
    }
    return libPromise;
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  // Logitech stops spinning once you take hold of it and does not start again.
  function stopSpinOnInteraction(mv) {
    mv.addEventListener('camera-change', function (e) {
      if (e.detail && e.detail.source === 'user-interaction') {
        mv.autoRotate = false;
        mv.removeAttribute('auto-rotate');
      }
    });
  }

  function Viewer(tile) {
    var cfg = {
      src: tile.dataset.model,
      tour: tile.dataset.tour,
      lib: tile.dataset.lib,
      qr: tile.dataset.qr,
      alt: tile.dataset.modelAlt || '',
      bestView: tile.dataset.bestView || '38deg 72deg auto'
    };

    var modal, sheet, stage, mv, data, bar, card, reachBtns = [], note, arBtn, tourBtn, qr;
    var arCtl, arNote;
    var current = 0;

    function build() {
      modal = el('div', 'ia3d-modal');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', '3D viewer');
      sheet = el('div', 'ia3d-sheet');
      stage = el('div', 'ia3d-stage');
      var close = el('button', 'ia3d-close', '&times;');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.addEventListener('click', hide);
      stage.appendChild(el('div', 'ia3d-spinner', 'Loading the model'));
      sheet.appendChild(close);
      sheet.appendChild(stage);
      modal.appendChild(sheet);
      document.body.appendChild(modal);

      modal.addEventListener('click', function (e) { if (e.target === modal) hide(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) hide();
      });
    }

    // The class on <html> is what gets the sales chat bubble out of the way.
    // It is fixed to the same bottom right corner at z-index 2147483000, so
    // on any window under about 900px wide it sits on top of this panel, and
    // its own 374px chat sheet would cover half the model. Two things cannot
    // own one corner; the viewer is a deliberate full attention mode and the
    // chat comes back the moment it closes.
    function hide() {
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('ia3d-open');
      document.documentElement.style.overflow = '';
      tile.focus();
    }

    function show() {
      modal.classList.add('is-open');
      document.documentElement.classList.add('ia3d-open');
      document.documentElement.style.overflow = 'hidden';
    }

    // The stops drive the camera and the reach buttons, the same two things a
    // person drives by hand, so the tour can never show a state the buyer
    // cannot get back to on their own.
    function stops() { return (data && data.stops) || []; }
    function positions() { return (data && data.positions) || []; }

    function goPos(target) {
      if (target === current) return;
      mv.animationName = 'p' + current + 'p' + target;
      mv.play({ repetitions: 1 });   // Infinity is rejected and silently no-ops
      current = target;
      reachBtns.forEach(function (b) { b.classList.toggle('is-on', +b.dataset.pos === current); });
      note.textContent = positions()[current].note || '';
      if (arNote) arNote.textContent = positions()[current].label;
    }

    function buildControls() {
      bar = el('div', 'ia3d-bar');

      tourBtn = el('button', 'ia3d-tourbtn', '&#9654; Show me around');
      tourBtn.type = 'button';
      bar.appendChild(tourBtn);
      bar.appendChild(el('span', 'ia3d-bar__sep'));

      bar.appendChild(el('label', null, 'Reach'));
      var group = el('div', 'ia3d-reachbtns');
      positions().forEach(function (p, i) {
        var b = el('button', i === 0 ? 'is-on' : null, p.label);
        b.type = 'button';
        b.dataset.pos = i;
        b.addEventListener('click', function () { goPos(i); });
        group.appendChild(b);
        reachBtns.push(b);
      });
      bar.appendChild(group);

      note = el('span', 'ia3d-note', positions()[0].note || '');
      bar.appendChild(note);

      // AR belongs in this bar with everything else. It used to float in the
      // stage's bottom right corner, which is the one corner it could not
      // survive in: the sales chat bubble covers it on a narrow window, and
      // on a phone the bar itself does. A phone is the only place AR runs.
      bar.appendChild(el('span', 'ia3d-bar__sep'));
      arBtn = el('button', 'ia3d-arbtn', '&#9635; See it full size');
      arBtn.type = 'button';
      arBtn.hidden = true;
      arBtn.addEventListener('click', function () {
        if (mv.canActivateAR) mv.activateAR();
        else showQr(qr && qr.hidden);
      });
      bar.appendChild(arBtn);

      stage.appendChild(bar);

      buildArControls();
      buildQr();

      // Shown on a desktop too, where canActivateAR is false and always will
      // be, because that is where most people meet the product. There the
      // button hands the model to the phone in their pocket rather than
      // saying nothing at all and leaving the best thing the viewer does
      // undiscovered.
      var sync = function () { arBtn.hidden = !(mv.canActivateAR || qr); };
      mv.addEventListener('load', sync);
      mv.addEventListener('ar-status', sync);
      sync();

      buildTour();
    }

    // The control bar does not exist inside an AR session, so these two
    // buttons are a second copy of the only control that matters out there:
    // how far the boom is run out. They are children of <model-viewer>
    // because in a WebXR session model-viewer makes its own default slot the
    // XR DOM overlay root, which makes a child of it the only UI that
    // survives into the session. Button elements specifically: model-viewer's
    // beforexrselect handler looks for one in the event path and cancels the
    // XR select, so a tap here drives the boom instead of dragging it across
    // the yard. Hidden until ar-status says a session is running, and on iOS
    // never shown at all, because Quick Look is a native app that no DOM
    // reaches. See the README.
    function buildArControls() {
      if (positions().length < 2) return;
      var last = positions().length - 1;

      arCtl = el('div', 'ia3d-arctl');

      arCtl.appendChild(posBtn('Retract', 0));
      arNote = el('span', 'ia3d-arctl__now', positions()[current].label);
      arCtl.appendChild(arNote);
      arCtl.appendChild(posBtn('Extend', last));

      mv.appendChild(arCtl);
    }

    function posBtn(label, pos) {
      var b = el('button', pos === current ? 'is-on' : null, label);
      b.type = 'button';
      b.dataset.pos = pos;
      b.addEventListener('click', function () { goPos(pos); });
      reachBtns.push(b);
      return b;
    }

    function showQr(on) {
      if (!qr) return;
      qr.hidden = !on;
      arBtn.classList.toggle('is-on', !!on);
    }

    // A QR rather than a line of text telling someone to retype a URL. It is
    // generated by make_ar_qr.py and verified to decode before it ships; if
    // a product has no QR asset the image 404s and the option removes itself,
    // so a new product needs no code change either way.
    function buildQr() {
      if (!cfg.qr) return;
      qr = el('div', 'ia3d-qr');
      qr.hidden = true;
      qr.innerHTML =
        '<button type="button" class="ia3d-qr__x" aria-label="Close">&times;</button>' +
        '<b>See how far it reaches</b>' +
        '<p>AR runs on a phone. Scan this and the boom stands on the ground in ' +
        'front of you at full size. Run it out and back to check the reach you ' +
        'need.</p>' +
        '<img alt="" width="200" height="200">';
      var img = qr.querySelector('img');
      img.addEventListener('error', function () {
        qr.remove();
        qr = null;
        arBtn.hidden = !mv.canActivateAR;
      });
      img.src = cfg.qr;
      qr.querySelector('.ia3d-qr__x').addEventListener('click', function () { showQr(false); });
      stage.appendChild(qr);
    }

    function buildTour() {
      if (!stops().length) { tourBtn.remove(); return; }

      card = el('div', 'ia3d-card');
      card.hidden = true;
      card.innerHTML =
        '<b></b><p></p><div class="ia3d-dots"></div>' +
        '<div class="ia3d-nav">' +
        '<button type="button" data-act="prev" title="Back">&#8249;</button>' +
        '<button type="button" data-act="play" title="Pause">&#10073;&#10073;</button>' +
        '<button type="button" data-act="next" title="Next">&#8250;</button>' +
        '<button type="button" data-act="exit" class="ia3d-exit">Done</button>' +
        '</div>';
      stage.appendChild(card);

      var title = card.querySelector('b'),
        line = card.querySelector('p'),
        dots = card.querySelector('.ia3d-dots'),
        prev = card.querySelector('[data-act="prev"]'),
        play = card.querySelector('[data-act="play"]'),
        next = card.querySelector('[data-act="next"]'),
        exit = card.querySelector('[data-act="exit"]');

      dots.innerHTML = stops().map(function () { return '<i></i>'; }).join('');
      var bars = Array.prototype.slice.call(dots.children);
      var at = -1, timer = null, running = false;

      // Every stop was framed on a landscape stage, and a boom is a wide thing,
      // so on a phone held upright the subject runs off both edges. Field of
      // view is the VERTICAL angle, so widening it both distorts the close
      // stops and hits the viewer's own 45 degree ceiling. Backing the camera
      // off keeps the horizontal extent of every stop exactly as framed.
      var AIMED_AT = 4 / 3;
      function widen() {
        return Math.max(1, AIMED_AT / (mv.clientWidth / Math.max(mv.clientHeight, 1)));
      }
      function orbitFor(orbit) {
        var m = orbit.match(/^(.*\s)(\d+(?:\.\d+)?)m$/);
        return m ? m[1] + (parseFloat(m[2]) * widen()).toFixed(2) + 'm' : orbit;
      }

      function clear() { clearTimeout(timer); timer = null; }

      function paint() {
        bars.forEach(function (b, i) {
          b.classList.toggle('is-on', i === at);
          b.classList.toggle('is-done', i < at);
        });
        prev.disabled = at <= 0;
        next.disabled = at >= stops().length - 1;
        play.innerHTML = running ? '&#10073;&#10073;' : '&#9654;';
        play.title = running ? 'Pause' : 'Play';
      }

      function go(i) {
        clear();
        at = Math.max(0, Math.min(stops().length - 1, i));
        var s = stops()[at];
        title.textContent = s.title;
        line.textContent = s.line;
        goPos(s.pos);
        mv.cameraTarget = s.target;
        mv.cameraOrbit = orbitFor(s.orbit);
        mv.fieldOfView = s.fov;
        paint();
        // The last stop does not roll over to the first. A tour that loops
        // reads as a screensaver and the buyer stops treating it as something
        // said to them. It just stops, with the controls still there.
        if (running && at < stops().length - 1) {
          timer = setTimeout(function () { go(at + 1); }, s.dwell);
        } else if (running) {
          running = false;
          paint();
        }
      }

      function open() {
        running = true;
        mv.autoRotate = false;
        mv.removeAttribute('auto-rotate');
        mv.interpolationDecay = 140;        // a glide between stops, not a cut
        mv.minFieldOfView = '5deg';         // the label stops need a tighter frame
        mv.maxCameraOrbit = 'auto auto 60m';
        card.hidden = false;
        showQr(false);
        bar.style.visibility = 'hidden';
        go(0);
      }

      function close() {
        clear();
        running = false;
        mv.interpolationDecay = 60;
        mv.minFieldOfView = '12deg';
        mv.maxCameraOrbit = 'auto auto auto';
        mv.cameraTarget = 'auto';
        mv.fieldOfView = 'auto';
        mv.cameraOrbit = cfg.bestView;
        card.hidden = true;
        bar.style.visibility = '';
      }

      window.addEventListener('resize', function () {
        if (!card.hidden && at >= 0) mv.cameraOrbit = orbitFor(stops()[at].orbit);
      });

      tourBtn.addEventListener('click', open);
      exit.addEventListener('click', close);
      prev.addEventListener('click', function () { go(at - 1); });
      next.addEventListener('click', function () { go(at + 1); });
      play.addEventListener('click', function () {
        running = !running;
        if (running) go(at >= stops().length - 1 ? 0 : at + 1);
        else { clear(); paint(); }
      });

      // Taking hold of the model pauses the tour where it is. Someone who stops
      // to look at a weld usually wants to carry on afterwards.
      mv.addEventListener('camera-change', function (e) {
        if (!card.hidden && running && e.detail && e.detail.source === 'user-interaction') {
          running = false;
          clear();
          paint();
        }
      });
    }

    var started = false;

    function start() {
      show();
      if (started) return;
      started = true;

      Promise.all([
        loadLib(cfg.lib),
        fetch(cfg.tour, { credentials: 'same-origin' }).then(function (r) { return r.json(); })
      ]).then(function (res) {
        data = res[1];
        stage.innerHTML = '';

        mv = document.createElement('model-viewer');
        mv.setAttribute('src', cfg.src);
        mv.setAttribute('alt', cfg.alt);
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('interaction-prompt', 'none');
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('shadow-softness', '0.9');
        mv.setAttribute('exposure', '1.05');
        mv.setAttribute('environment-image', 'neutral');
        mv.setAttribute('camera-orbit', cfg.bestView);
        mv.setAttribute('min-field-of-view', '12deg');
        mv.setAttribute('interpolation-decay', '60');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
        mv.setAttribute('ar-scale', 'fixed');
        mv.setAttribute('ar-placement', 'floor');
        stage.appendChild(mv);

        stopSpinOnInteraction(mv);
        buildControls();
      }).catch(function (err) {
        stage.innerHTML = '';
        stage.appendChild(el('div', 'ia3d-spinner', 'The 3D model could not be loaded.'));
        console.error('[ia-media-3d]', err);
      });
    }

    build();
    tile.addEventListener('click', start);
  }

  function init() {
    document.querySelectorAll('.ia-3d-tile').forEach(function (tile) {
      if (tile.dataset.iaReady) return;
      tile.dataset.iaReady = '1';
      Viewer(tile);
    });

    // Arrived by scanning the QR off a desktop screen, or by following
    // the homepage link. Open the viewer on arrival, so the visitor
    // lands on the model instead of at the top of the page with the
    // tile still to be found. AR itself still takes the one tap on the
    // button, which is what browsers require anyway.
    //
    // Two spellings on purpose. view=ar is the QR, printed on steel in
    // the field and never to be broken. view=3d is the homepage link,
    // which is not promising AR: on a desktop it opens the viewer and
    // offers the QR, which is the honest thing to say to someone who
    // cannot run AR on the machine they are reading on.
    if (/[?&]view=(?:ar|3d)(?:&|$)/.test(location.search)) {
      var first = document.querySelector('.ia-3d-tile');
      if (first) first.click();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
